// src/components/dashboard/Sidebar.tsx
import React, { Fragment, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { Transition } from "@headlessui/react";
import { Dialog } from "@headlessui/react";

function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}

interface SidebarProps {
  userDetails: any;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  userDetails,
  sidebarOpen,
  setSidebarOpen,
}) => {
  const { jobId, appId } = useParams<{ jobId: string; appId: string }>();
  const { base_slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const userBaseIdMain = userDetails?.userBaseId || "";

  const navigation = [
    {
      name: "Application Details",
      to: `/${base_slug}/${jobId}/app/${appId}/details`,
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={24}
          height={24}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="icon icon-tabler icons-tabler-outline icon-tabler-users w-[1rem] h-[1rem] text-[#808080]"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" />
          <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          <path d="M21 21v-2a4 4 0 0 0 -3 -3.85" />
        </svg>
      ),
      current: location.pathname.includes(`/details`),
    },
    {
      name: "Tasks",
      to: `/${base_slug}/${jobId}/app/${appId}/tasks`,
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={24}
          height={24}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="icon icon-tabler icons-tabler-outline icon-tabler-square-check w-[0.9rem] h-[0.9rem] text-[#808080]"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M3 3m0 2a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z" />
          <path d="M9 12l2 2l4 -4" />
        </svg>
      ),
      current: location.pathname.includes(`/tasks`),
    },
    {
      name: "Messages",
      to: `/${base_slug}/${jobId}/app/${appId}/messages`,
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={24}
          height={24}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="icon icon-tabler icons-tabler-outline icon-tabler-message w-[1rem] h-[1rem] text-[#808080]"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M8 9h8" />
          <path d="M8 13h6" />
          <path d="M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-5l-5 3v-3h-2a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3h12z" />
        </svg>
      ),
      current: location.pathname.includes(`/messages`),
    },
    {
      name: "Ai Interview",
      to: `/${base_slug}/${jobId}/app/${appId}/ai_interview`,
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={24}
          height={24}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="icon icon-tabler icons-tabler-outline icon-tabler-robot w-[1rem] h-[1rem] text-[#808080]"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M4 8v-3a1 1 0 0 1 1 -1h2" />
          <path d="M4 16v3a1 1 0 0 0 1 1h2" />
          <path d="M20 8v-3a1 1 0 0 0 -1 -1h-2" />
          <path d="M20 16v3a1 1 0 0 1 -1 1h-2" />
          <path d="M8 8h8a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2v-4a2 2 0 0 1 2 -2z" />
          <path d="M9 12h6" />
          <path d="M9 9.5v3" />
          <path d="M15 9.5v3" />
        </svg>
      ),
      current: location.pathname.includes(`/ai_interview`),
    },
  ];

  return (
    <div>
      {/* Mobile hamburger menu button */}

      {/* mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50 lg:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-[82%] top-[-14px] flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                    </button>
                  </div>
                </Transition.Child>

                <div className="flex grow flex-col gap-y-4 overflow-y-auto bg-light-pink px-6 pb-4 py-4">
                  <div className="flex lg:hidden gap-[0.5rem] items-center cursor-pointer h-[32px] px-[0rem] w-full">
                    <img
                      className="h-6 w-6 rounded-[5px]"
                      src="https://pub-d78e6f485da34d3b9d6618927284f469.r2.dev/public/assets/teamer-logo.png"
                      alt="base logo"
                    />
                    <span className="text-sm text-black font-medium">
                      TeamerHr
                    </span>
                  </div>
                  <nav className="flex flex-1 flex-col">
                    <ul role="list" className="-mx-2 space-y-2">
                      {navigation.map((item) => (
                        <li key={item.name}>
                          <Link
                            to={item.to}
                            onClick={() => setSidebarOpen(false)} // ✅ Add this line
                            className={classNames(
                              item.current
                                ? "text-black-100 bg-[#00000014] hover:bg-[rgb(0,0,0,0.1)] cursor-pointer"
                                : "text-black-100 hover:text-black-100 hover:bg-gray-200 cursor-pointer",
                              "group flex gap-x-[10px] p-0 text-[13px] m-0 min-h-[28px] w-full cursor-pointer leading-5 items-center rounded-[0.375rem] font-medium text-wedges-gray-900 focus:outline-none px-2",
                            )}
                          >
                            {item.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div
        className="hidden lg:fixed top-[52px] lg:z-[9] lg:flex lg:w-[225px] lg:flex-col h-[calc(100vh_-62px)] rounded-tl-lg rounded-bl-lg"
        aria-label="Sidebar"
      >
        <div className="flex grow flex-col bg-white pt-[0] px-0 pb-4 sidebar-des border-r border-gray-100 rounded-tl-lg rounded-bl-lg">
          <nav className="flex flex-col px-[14px] mt-0">
            <ul role="list" className="flex flex-1 flex-col gap-y-2">
              <li>
                <ul role="list" className="space-y-1">
                  {navigation.map((item) => {
                    const isActive = item.current;
                    return (
                      <li
                        key={item.name}
                        className="h-[32px] mt-3 mb-[2px] py-[2px] sidebar-nav pl-[0px]"
                      >
                        <Link
                          to={item.to}
                          className={classNames(
                            isActive
                              ? "text-black-100 bg-gray-200 hover:bg-gray-300"
                              : "text-black-100 hover:text-black-100 hover:bg-gray-100",
                            "group flex gap-x-[10px] p-0 text-[13px] m-0 min-h-[32px] w-full cursor-pointer leading-5 items-center rounded-[0.375rem] font-medium text-wedges-gray-900 focus:outline-none px-2",
                          )}
                        >
                          {typeof item.icon === "string" ? (
                            <span
                              className={classNames(
                                isActive
                                  ? "text-[#0000007a]"
                                  : "text-[#0000007a] group-hover:text-black-100",
                                "h-[1.1em] w-[1.1em] shrink-0",
                              )}
                            >
                              {item.icon}
                            </span>
                          ) : (
                            React.cloneElement(item.icon, {
                              "aria-hidden": "true",
                              className: classNames(
                                isActive
                                  ? "text-[#0000007a]"
                                  : "text-[#0000007a] group-hover:text-black-100",
                                "h-[1.1em] w-[1.1em] shrink-0",
                              ),
                            })
                          )}
                          {item.name}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
